import DogCuteFailed from "@/assets/images/dog-cute-failed.png"
import { RestakingResult } from "@/hooks/useRestaking"
import {
  CLAIM_STAKING,
  ERRORS,
  IS_MAINNET,
  RENEC_EXPLORER_URL,
  SOLSCAN_EXPLORER_URL,
  STAKING,
  STOP_UNSTAKING,
  UNSTAKING,
  WITHDRAW_UNSTAKING,
} from "@/utils/constants"
import { HelperText, RestakingAccountState, TransactionDetail } from "@/utils/dataTypes"
import { replaceLocaleStringWithValues, stringifyVariable, waitForTransactionConfirmation } from "@/utils/helpers"
import { sendEventLog } from "@/utils/tracking-events"
import { Typography } from "@mui/material"
import { BlockchainNetwork } from "@renec-foundation/relend-adapter"
import dayjs from "dayjs"
import Rollbar from "rollbar"

const ACCOUNT_FEE = 0.002
const FEE_AMOUNT = 0.001
const TRANSACTION_FEE = 0.000005
const OTHER_FEES = 0.0035

export const DEFAULT_HELPER_TEXT: HelperText = { content: "", color: "" }

export const DEFAULT_TRANSACTION_DETAIL: TransactionDetail = {
  isActive: false,
  ctaButtonLink: "",
  title: "",
  content: "",
}

export const verifyStakeUnstakeAction = (
  value: number | undefined,
  tokenBalance: number,
  totalStaked: number,
  type: typeof STAKING | typeof UNSTAKING,
  trans: any,
) => {
  if (!value || value <= 0) {
    return trans.staking.errors.please_enter_amount
  }

  if (type === STAKING) {
    if (value > tokenBalance) {
      return trans.staking.errors.not_enough_balance
    }
  }

  if (type === UNSTAKING) {
    if (value > totalStaked) {
      return trans.staking.errors.cannot_unstake_above_staked_amount
    }
  }

  return null
}

export const calculateTimeRemaining = (targetTimestamp: EpochTimeStamp) => {
  const now = dayjs()
  const target = dayjs(targetTimestamp * 1000)
  const duration = target.diff(now, "s")
  if (duration <= 0) return null
  const days = Math.floor(duration / 86400)
  const hours = Math.floor((duration % 86400) / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  const seconds = duration % 60
  const timeComponents = []
  if (days > 0) timeComponents.push(`${days.toString().padStart(2, "0")}d`)
  if (timeComponents.length > 0 || hours > 0) timeComponents.push(`${hours.toString().padStart(2, "0")}h`)
  if (timeComponents.length > 0 || minutes > 0) timeComponents.push(`${minutes.toString().padStart(2, "0")}m`)
  timeComponents.push(`${seconds.toString().padStart(2, "0")}s`)
  return timeComponents.join(" : ")
}

export const handleRestakingSuccessful = async (
  network: BlockchainNetwork,
  amount: number,
  symbol: string | undefined,
  result: RestakingResult,
  setTransactionDetail: React.Dispatch<React.SetStateAction<TransactionDetail>>,
  walletAddress: string | undefined,
  type: typeof STAKING | typeof UNSTAKING | typeof STOP_UNSTAKING | typeof WITHDRAW_UNSTAKING | typeof CLAIM_STAKING,
  trans: any,
  refreshStakingData: () => void,
) => {
  if (!result.isSucceeded) {
    return
  }

  const IS_STAKING = type === STAKING
  const IS_UNSTAKING = type === UNSTAKING
  const IS_STOP_UNSTAKING = type === STOP_UNSTAKING
  const IS_WITHDRAW_UNSTAKING = type === WITHDRAW_UNSTAKING
  // await sleep(700) // neeed to wait for the transaction to be confirmed / finalized
  const txHash = result.tx
  await waitForTransactionConfirmation(network, txHash || "")

  // google events
  const eventName = IS_STAKING
    ? "finish_staking"
    : IS_UNSTAKING
      ? "finish_unstaking"
      : IS_STOP_UNSTAKING
        ? "finish_stop_unstaking"
        : IS_WITHDRAW_UNSTAKING
          ? "finish_withdraw_unstaking"
          : "finish_claim_staking"
  sendEventLog(eventName, {
    dapp_name: "renec_lend",
    result: "approved",
    wallet: walletAddress,
    tx: txHash,
    isMainnet: IS_MAINNET,
  })

  const explorerURL = network === BlockchainNetwork.SOLANA ? SOLSCAN_EXPLORER_URL : RENEC_EXPLORER_URL
  const cluster = IS_MAINNET ? "" : network === BlockchainNetwork.SOLANA ? "devnet" : "testnet"
  const link = `${explorerURL}/tx/${txHash}?cluster=${cluster}`
  const modalSucceedTitle = IS_STAKING
    ? trans.staking.modal.stake_successfully
    : IS_UNSTAKING
      ? trans.staking.modal.unstake_successfully
      : IS_STOP_UNSTAKING
        ? trans.staking.modal.stop_unstaking_successfully
        : IS_WITHDRAW_UNSTAKING
          ? trans.staking.modal.withdraw_successfully
          : trans.staking.modal.claim_reward_successfully
  const modalContent = IS_STAKING
    ? trans.staking.modal.you_just_staked
    : IS_UNSTAKING
      ? trans.staking.modal.you_just_unstaked
      : IS_STOP_UNSTAKING
        ? trans.staking.modal.you_just_stop_unstaked
        : IS_WITHDRAW_UNSTAKING
          ? trans.staking.modal.you_just_withdraw
          : trans.staking.modal.you_just_claim_reward

  setTransactionDetail({
    isActive: true,
    ctaButtonLink: link,
    title: modalSucceedTitle,
    content: (
      <>
        {modalContent}{" "}
        <Typography component='span' fontWeight={700}>
          {amount} {symbol}
        </Typography>
      </>
    ),
  })
  refreshStakingData()
}

export const handleRestakingError = (
  amount: number,
  symbol: string | undefined,
  restakingAccount: null | RestakingAccountState,
  result: RestakingResult,
  setHelperText: React.Dispatch<React.SetStateAction<HelperText>> | undefined,
  setTransactionDetail: React.Dispatch<React.SetStateAction<TransactionDetail>>,
  rollbar: Rollbar,
  walletAddress: string | undefined,
  type: typeof STAKING | typeof UNSTAKING | typeof STOP_UNSTAKING | typeof WITHDRAW_UNSTAKING | typeof CLAIM_STAKING,
  trans: any,
) => {
  const defaultMessage = trans.errors.default_error
  const error = result.error?.toString()
  const isTransactionDenied = error === ERRORS.DENY_TRANSACTION || error === ERRORS.REFUSE_TRANSACTION
  const errorMessage = isTransactionDenied ? trans.errors.deny_transaction : error ?? defaultMessage
  const IS_WITHDRAW_UNSTAKING = type === WITHDRAW_UNSTAKING

  const isInsufficientBalance =
    errorMessage === ERRORS.INSUFFIENT_BALANCE || errorMessage?.includes(ERRORS.ATTEMPT_BUT_NO_PRIOR_CREDIT)
  if (isInsufficientBalance) {
    /**
     * Calculate the total fee based on restaking account status, unstaked amount, transaction type, and additional fees
     * - If we are staking and there is an unstaked amount, we need to stop the unstaking process and then stake
     **/
    const baseFee = restakingAccount
      ? restakingAccount.unstakedAmount.toNumber() > 0 && type === STAKING
        ? FEE_AMOUNT * 2 // Stop unstaking + staking
        : FEE_AMOUNT // Standard fee amount
      : FEE_AMOUNT + ACCOUNT_FEE
    const fee = baseFee + TRANSACTION_FEE + OTHER_FEES
    setTransactionDetail({
      isActive: true,
      ctaButtonLink: "https://nemo.renec.org/swap/reUSD_RENEC",
      title: trans.staking.errors.failed_to_complete,
      ctaButtonTitle: trans.staking.errors.deposit_more_renec,
      isDisplayingGoHomeButton: false,
      content: replaceLocaleStringWithValues(trans.staking.errors.please_deposit_renec, fee.toString()),
      resultImage: DogCuteFailed,
    })
  }

  if (setHelperText) {
    setHelperText({ content: errorMessage, color: "red" })
  }

  if (IS_WITHDRAW_UNSTAKING && !isInsufficientBalance) {
    setTransactionDetail({
      isActive: true,
      title: trans.staking.errors.failed_to_complete,
      content: errorMessage,
      isDisplayingGoHomeButton: true,
      resultImage: DogCuteFailed,
    })
  }

  const shouldLogError = error && !isTransactionDenied
  if (shouldLogError) {
    rollbar.error(
      `RELEND: Type: ${type}. ${walletAddress}: ${stringifyVariable(error)}. Amount: ${amount}. ${symbol ? `Symbol: ${symbol}.` : null}`,
    )
  }
}
